import React, { useEffect, useMemo, useState } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import getBuilderStateContentVariantData from '~/builder/utils/getBuilderStateContentVariantData';
import { shouldApplyAutoSizeFilteringByBuilderConfig } from '~/builder/utils/getProductGridItemFiltersAndSortOptions';
import { useGridVariant } from '~/builder/utils/gridVariantContext';
import ProductBrowser from '~/grid/components/ProductBrowser';
import StyleGuide from '~/grid/components/StyleGuide';
import StyleGuideFilterPills from '~/grid/components/StyleGuideFilterPills/StyleGuideFilterPills';
import GridScrollContext from '~/grid/context/GridScrollContext';
import StyleGuideContext from '~/grid/context/StyleGuideContext';
import {
  transformCioFacets,
  transformCioSortOptions,
  transformCioResults,
  getUrlFilters,
  getSanitizedFilterInputsForCio,
  cioFacetsToUrlFilters,
  getPreFilterExpressions,
  getCioSortOrder,
  translateFilterTags,
  updateLocalizedLabel,
  transformAssetContainer,
  applyAutoFilters,
  getCioSortOptionValuesFromBuilderSortOptions,
  isolateSizeFilters,
  translateSortOptions,
} from '~/grid/utils/cioUtils';
import useProfileSizes from '~/pdp/utils/useProfileSizes';
import { breakpoints } from '~/search/constants/searchGrid';
import featureNames from '~/shared/constants/featureFlags';
import { gridColumnSettings, pageSize } from '~/shared/constants/productGrid';
import useAssortmentTestConfigs from '~/shared/utils/useAssortmentTestConfigs';
import { useCioClient } from '~/shared/utils/useCioClient';
import useGender from '~/shared/utils/useGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  useBreakpoint,
  LoadingStatus,
  mobile,
  PlaceholderGrid,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import { parseProductListingQueryParamsCustomFilters } from '~/techstyle-shared/react-products';
import { useDomain } from '~/techstyle-shared/redux-core';

import {
  ConstructorGridFilterNames,
  ConstructorGridFilterTypes,
} from './BuilderConstructorGrid.builder';

const loadingStatusContainerStyle = css`
  left: 50%;
`;

const MainContent = styled.main`
  max-width: 1504px;
  margin: 0 auto;
  padding: 42px 0;
  width: 100%;

  ${mobile`
    padding: 0;
  `}
`;

const MobileGridHeader = styled.h1`
  padding: 16px 16px 0 16px;
  ${({ theme }) => theme.mobileGridHeaderStyle}
`;

const ProductBrowserWrapper = styled.div`
  max-width: 1330px;
  margin: 0 auto;
`;

const labelStyle = css`
  border-bottom: 1px solid;
  padding-bottom: 2px;
`;

const InitialLoadFableticsPlaceholderGrid = styled(PlaceholderGrid)`
  --chinHeight: 114px;
  margin-bottom: var(--rowGap);
  max-width: 75%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 100px;
`;

const BuilderConstructorGrid = ({
  builderState,
  assetContainer,
  filterType,
  collectionSelection,
  collectionIdOverride,
  categorySelection,
  categoryIdOverride,
  facet,
  facetOption,
  gridGender,
  gridHeader,
  gridStyleGuide,
  sortOptions,
  productSource,
}) => {
  const cioClient = useCioClient();
  const { isSaleGrid } = useGridVariant();

  const trimmedProductSource = productSource?.trim();
  const cioProductSource = trimmedProductSource?.endsWith('-cio')
    ? trimmedProductSource
    : `${trimmedProductSource}-cio`;

  const { isMobile } = useBreakpoint();
  const { isVip, isElite } = useMembership();
  const isSecondarySite = useSecondarySite();
  const { locale } = useIntl();
  const { profileSizes } = useProfileSizes({ isPdp: false });
  const { gender } = useGender();
  const domain = useDomain();
  const { tld } = domain;
  const { excludeFplCodes, excludeTagIds } = useAssortmentTestConfigs();

  const { enabled: shouldUseImageService } = useHasFeature(
    featureNames.SHOULD_USE_IMAGE_SERVICE_URL
  );
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const { enabled: surfaceHiddenFacets } = useHasFeature(
    featureNames.SURFACE_HIDDEN_FACETS
  );

  const variantData = getBuilderStateContentVariantData({
    builderStateContent: builderState?.content,
  });
  const builderAutomaticSizeFiltering = variantData?.automaticSizeFiltering;

  const staticCurrentDate = new Date();

  let cioFilterName;
  let cioFilterValue;
  if (filterType === ConstructorGridFilterTypes.Collection) {
    cioFilterName = ConstructorGridFilterNames.Collection;
    cioFilterValue = collectionIdOverride || collectionSelection?.value;
  }

  if (filterType === ConstructorGridFilterTypes.Category) {
    cioFilterName = ConstructorGridFilterNames.Category;
    cioFilterValue = categoryIdOverride || categorySelection?.value;
  }

  if (filterType === ConstructorGridFilterTypes.Facet) {
    cioFilterName = facet;
    cioFilterValue = facetOption;
  }

  const hasStyleGuide =
    !!gridStyleGuide?.assetContainers?.desktop &&
    !!gridStyleGuide?.assetContainers?.mobile &&
    !!gridStyleGuide?.tagList;
  const styleGuideTagList = hasStyleGuide
    ? `tag_${gridStyleGuide.tagList}_list`
    : null;

  const [cioBrowseState, setCioBrowseState] = useState({
    initialProducts: {},
    totalCount: 0,
    filterSettings: [],
    sortOptions: [],
    initialSort: {},
    isLoading: true,
    error: null,
  });
  const [baseFilters, setBaseFilters] = useState({
    aggregations: [],
  });

  const { query, push } = useRouter();
  const noBrowseResults = cioBrowseState.totalCount === 0;
  const gridSlotsAsset = transformAssetContainer({
    assetContainer,
  });

  const placeholderPropsGrid = isMobile
    ? gridColumnSettings.mobile
    : gridColumnSettings.desktop;

  const { filters: initialFilters } = useMemo(() => {
    return parseProductListingQueryParamsCustomFilters(query, {
      baseFilters,
      filterSettings: cioBrowseState.filterSettings,
      sortOptions: [],
    });
  }, [query, baseFilters, cioBrowseState.filterSettings]);

  const shouldAutofilter = useMemo(() => {
    return shouldApplyAutoSizeFilteringByBuilderConfig({
      builderAutomaticSizeFiltering,
      gridGender,
      customerGender: gender,
    });
  }, [builderAutomaticSizeFiltering, gridGender, gender]);

  const initialFiltersWithAutofilters = useMemo(() => {
    if (shouldAutofilter) {
      return applyAutoFilters({
        initialFilters,
        profileSizes,
        gender,
        builderAutomaticSizeFiltering,
      });
    }
    return initialFilters;
  }, [
    builderAutomaticSizeFiltering,
    gender,
    initialFilters,
    profileSizes,
    shouldAutofilter,
  ]);

  const translationDict = useMemo(() => {
    const dict = {};
    cioBrowseState.filterSettings.forEach((filter) => {
      filter.items.forEach((item) => {
        dict[item.value] = item.label;
      });
    });
    return dict;
  }, [cioBrowseState.filterSettings]);

  useEffect(() => {
    /* This is a temporary solution for translating the facets using CIO translations,
    intended to be used until we can transfer the filter components from the shared packages
    to the brand codebase. The work for this transfer is outlined in https://techstyle-prod.atlassian.net/browse/FLD-2020 */
    if (typeof window !== 'undefined') {
      updateLocalizedLabel({
        querySelector: '[data-autotag="filter-size"] [class*="TextComponent"]',
        localizationKey: 'filter-size',
        locale,
      });
      updateLocalizedLabel({
        querySelector:
          '[class*="FilterTagsCustomFilters__HeaderLabelWrapper"] [class*="TextComponent"]',
        localizationKey: 'filtered-by',
        locale,
      });
      updateLocalizedLabel({
        querySelector:
          '[class*="FilterTagsCustomFilters__ClearAllButton"] [class*="TextComponent"]',
        localizationKey: 'clear-all',
        locale,
      });
      translateFilterTags({
        translationDict,
        querySelector:
          '[data-autotag="filtered-by-option"] [class*="TextComponent"]',
      });
    }
    /* needed to add query as a trigger here, since the translationDict doesn't always change when a new filter is selected,
  which results in the translation logic not triggering when user adds or remove filter. The query is guaranteed to change
  whenever user interacts with the filters */
  }, [query, locale, translationDict]);

  const profileSizeAutoFilters = useMemo(() => {
    if (!shouldAutofilter) {
      return null;
    }

    const filtersObject = applyAutoFilters({
      initialFilters,
      profileSizes,
      gender,
      builderAutomaticSizeFiltering,
    });

    // this parses the filtersObject to an object of size arrays, which is expected by the CIO API
    const result = Object.entries(filtersObject.aggregationFilter).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          acc[key] = value; // Only add key if the array is not empty
        }
        return acc;
      },
      {}
    );

    return Object.keys(result).length === 0 ? null : result;

    // we need to ignore the initialFilters in the dependency array in order to prevent an infinite loop
    // TODO: figure out a way so we don't have work around missing dependencies, tracking ticket here: https://techstyle-prod.atlassian.net/browse/FLD-2113
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderAutomaticSizeFiltering, gender, profileSizes, shouldAutofilter]);

  useEffect(() => {
    const getCioBrowseResults = async () => {
      try {
        setCioBrowseState((prev) => ({ ...prev, isLoading: true }));

        // We need to get a list of all the possible filters from the cio api
        // so that the filters will show up in the url query params when selected
        // and removed when cleared via individual filters or the "clear all" button.
        // This only fires on the initial SSR load. The base filters will not need to
        // be re-queried across SPA route changes.
        const cioFacetResults = await cioClient.browse.getBrowseFacets({
          page: 1,
          resultsPerPage: 200,
        });
        const aggregations = cioFacetResults?.response?.facets?.map(
          (facet) => ({
            field: cioFacetsToUrlFilters[facet.name] || facet.name,
          })
        );
        if (aggregations?.length) {
          setBaseFilters({ aggregations });
        }

        const whitelistedCioFilters = aggregations?.map(({ field }) => field);
        const { urlSort, urlFilters } = getUrlFilters({
          query,
          whitelistedCioFilters,
        });
        const { sizeFilters, nonSizeFilters } = isolateSizeFilters(urlFilters);

        const translatedSortOptions = translateSortOptions(sortOptions);
        const defaultSort = urlSort || translatedSortOptions?.[0] || null;
        const hiddenFacets = config.get(
          'public.brand.constructor.hiddenFacets'
        );

        const browseParameters = {
          resultsPerPage: pageSize,
          preFilterExpression: getPreFilterExpressions({
            isElite,
            isVip,
            dateNowInMs: staticCurrentDate.getTime(),
            shouldShowWarehouseProducts: isSecondarySite,
            shouldHideOriginRestricted: removeOriginFlag,
            selectedSizeFilters: profileSizeAutoFilters || sizeFilters,
            tld,
            autoExcludeClearanceFplIds: !isSaleGrid,
            excludeFplCodes,
            excludeTagIds,
          }),
          filters: getSanitizedFilterInputsForCio(nonSizeFilters),
          sortBy: defaultSort,
          sortOrder: getCioSortOrder(defaultSort),
        };

        if (surfaceHiddenFacets && hiddenFacets?.length) {
          browseParameters.hiddenFacets = hiddenFacets;
        }

        const results = await cioClient.browse.getBrowseResults(
          cioFilterName,
          cioFilterValue,
          browseParameters
        );

        if (results) {
          const redirectUrl = results?.response?.redirect?.data?.url;
          if (redirectUrl) {
            return push(redirectUrl);
          }

          const transformedCioFilterSettings = transformCioFacets({
            facets: results?.response?.facets,
            requestFilters: results?.request?.filters,
            domain,
          });

          const transformedCioSortOptions = transformCioSortOptions({
            sortOptions: results?.response?.sort_options,
            isVip,
            defaultSort,
          });

          /* Here we are mapping user chosen builder sort options to cio sort options,
          only the subset of user selected cio sort options will be available in the dropdown */
          const modifiedSortOptions =
            getCioSortOptionValuesFromBuilderSortOptions({
              builderSortOptions: sortOptions,
              sortOptions: transformedCioSortOptions,
            });

          const transformedCioBrowseResults = await transformCioResults({
            results: results?.response?.results,
            shouldUseImageService,
          });

          const totalResults = results?.response?.total_num_results ?? 0;

          const initialSortOption = modifiedSortOptions.find(
            (option) => option.selected
          );

          setCioBrowseState({
            initialProducts: {
              aggregations: transformedCioFilterSettings,
              page: 1,
              pageSize,
              pages: Math.ceil(totalResults / pageSize),
              products: transformedCioBrowseResults,
              total: totalResults,
            },
            totalCount: results?.response?.total_num_results,
            filterSettings: transformedCioFilterSettings,
            sortOptions: modifiedSortOptions,
            initialSort: initialSortOption || modifiedSortOptions?.[0] || {},
            isLoading: false,
          });
        } else {
          setCioBrowseState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        setCioBrowseState((prev) => ({ ...prev, isLoading: false, error }));
      }
    };

    if (cioClient) {
      getCioBrowseResults();
    }
    // We currently have to ignore `query` from the dependencies because we don't
    // want to re-fetch the browse results when the filters change. This should be
    // handled within product browser loadMoreItems(). We only want to refetch the
    // init products when the page changes.
    // TODO: figure out a way so we don't have work around missing dependencies, tracking ticket here: https://techstyle-prod.atlassian.net/browse/FLD-2113
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cioClient, isVip, Boolean(profileSizeAutoFilters), tld]);

  if (cioBrowseState?.isLoading || cioBrowseState?.error) {
    return (
      <InitialLoadFableticsPlaceholderGrid
        imageAspectRatio={1}
        data-autotag="empty-grid-message"
        {...placeholderPropsGrid}
      />
    );
  }

  return (
    <MainContent>
      <GridScrollContext>
        <StyleGuideContext tagList={styleGuideTagList}>
          <ProductBrowserWrapper
            data-cnstrc-browse
            data-cnstrc-num-results={cioBrowseState?.totalCount}
            data-cnstrc-filter-name={cioFilterName}
            data-cnstrc-filter-value={cioFilterValue}
          >
            <LoadingStatus>
              <ProductBrowser.LoadingGridsSpinner
                containerStyle={
                  noBrowseResults ? loadingStatusContainerStyle : null
                }
              />
              <ProductBrowser
                baseFilters={baseFilters}
                initialFilters={initialFiltersWithAutofilters || initialFilters}
                filterSettings={cioBrowseState?.filterSettings}
                sortOptions={cioBrowseState?.sortOptions}
                initialSortOption={cioBrowseState?.initialSort}
                // In ProductBrowser, we don't currently correctly reset the
                // `productsReducer` state when the component is re-rendered due
                // to a SPA navigation. So even though we'd want its products
                // and `payloadKey` to be re-initialized with the new content
                // from `getInitialProps`, it instead keeps the existing state
                // mounted and thinks the `payloadKey` changed, thus triggering
                // a duplicate request for the first page of products (which
                // already happened in `getInitialProps`). We can fix this, but
                // it will take some work on that component's state lifecycle
                // and maybe adding some refs... for now we instead remount the
                // entire ProductBrowser when the route changes (as determined
                // by `getInitialProps` being called, which will increment the
                // `productBrowserKey`). You might think we can instead use,
                // say, `routeInfo.canonicalPath`, or the serialized
                // `initialFilters` or some prop we already have anyway, but
                // it's possible for the user to re-navigate to the grid page
                // they're already on, so those wouldn't necessarily change.
                key={`${cioFilterName}-${cioFilterValue}`}
              >
                {hasStyleGuide && (
                  <StyleGuide
                    containerName={
                      isMobile
                        ? gridStyleGuide.assetContainers.mobile
                        : gridStyleGuide.assetContainers.desktop
                    }
                    leadAsset={gridStyleGuide.assetContainers.marketingCard}
                  />
                )}

                {isMobile && gridHeader && (
                  <MobileGridHeader>{gridHeader}</MobileGridHeader>
                )}

                {hasStyleGuide && isMobile && <StyleGuideFilterPills />}

                <ProductBrowser.Header
                  labelStyle={labelStyle}
                  label={gridHeader}
                />
                <ProductBrowser.Body breakpoints={breakpoints}>
                  {!noBrowseResults ? <ProductBrowser.SideBar /> : null}
                  {!noBrowseResults && isMobile ? (
                    <ProductBrowser.FilterDrawer />
                  ) : null}
                  <ProductBrowser.GridContent>
                    <ProductBrowser.ProductGrid
                      initialProducts={cioBrowseState?.initialProducts}
                      initialSortOption={cioBrowseState?.initialSort}
                      productSource={
                        trimmedProductSource
                          ? cioProductSource
                          : 'constructorio-grid'
                      }
                      isCioBrowse
                      isSaleGrid={isSaleGrid}
                      cioFilterName={cioFilterName}
                      cioFilterValue={cioFilterValue}
                      gridSlotsAsset={gridSlotsAsset}
                    />
                  </ProductBrowser.GridContent>
                </ProductBrowser.Body>
              </ProductBrowser>
            </LoadingStatus>
          </ProductBrowserWrapper>
        </StyleGuideContext>
      </GridScrollContext>
    </MainContent>
  );
};

BuilderConstructorGrid.propTypes = {
  assetContainer: PropTypes.object,
  builderState: PropTypes.object,
  categoryIdOverride: PropTypes.string,
  categorySelection: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  collectionIdOverride: PropTypes.string,
  collectionSelection: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  facet: PropTypes.string,
  facetOption: PropTypes.string,
  filterType: PropTypes.string.isRequired,
  gridGender: PropTypes.string,
  gridHeader: PropTypes.string,
  gridStyleGuide: PropTypes.object,
  productSource: PropTypes.string,
  sortOptions: PropTypes.array,
};

export default BuilderConstructorGrid;
